import React, { useState, useEffect, useRef } from 'react';

export const CredibilitySection = ({
  sectionTitle = "You're in Good Hands",
  backgroundColor = "#f1eeee",
  imageUrl = "/founders-illustrated.png",
  imagePlaceholderText = "Founders",
  imageCaption = "Co-founders Jen Horonjeff, PhD and Ronnie Sharpe",
  credential1 = "Founded by lifelong chronic illness patients who know what it's like to navigate the healthcare system",
  credential2 = "Built on the belief that patient experiences deserve to be heard, valued, and rewarded fairly",
  credential3 = "Designed to be inclusive so every voice helps shape better treatments, research, and care",
  credential4 = "Your privacy is sacred—HIPAA-compliant, transparent, and shared only with your consent",
  ctaText = "Read Our Story",
  ctaLink = "#",
  onApproachClick
}) => {
  const credentials = [credential1, credential2, credential3, credential4].filter(Boolean);
  const sectionRef = useRef(null);
  const [highlightAnimated, setHighlightAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setHighlightAnimated(true), 300);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Helper component for highlighted text
  const HighlightedText = ({ children, delay = 0 }) => (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <span style={{ position: 'relative', zIndex: 1 }}>{children}</span>
      <span
        style={{
          position: 'absolute',
          bottom: '2px',
          left: '-3px',
          right: '-3px',
          height: '40%',
          background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.5) 0%, rgba(245, 185, 165, 0.45) 40%, rgba(250, 200, 160, 0.4) 70%, rgba(245, 215, 170, 0.35) 100%)',
          zIndex: 0,
          transform: highlightAnimated ? 'scaleX(1)' : 'scaleX(0)',
          transformOrigin: 'left',
          transition: `transform 0.6s ease-out ${delay}s`,
          borderRadius: '2px',
          pointerEvents: 'none'
        }}
      />
    </span>
  );

  // Parse credentials to highlight specific phrases
  const renderCredential = (credential, index) => {
    // Credential 1: highlight "lifelong chronic illness patients"
    if (index === 0 && credential.includes('lifelong chronic illness patients')) {
      const parts = credential.split(/(lifelong chronic illness patients)/);
      return parts.map((part, i) => {
        if (part === 'lifelong chronic illness patients') {
          return <HighlightedText key={i} delay={0.2}>{part}</HighlightedText>;
        }
        return part;
      });
    }
    // Credential 2: highlight "heard, valued, and rewarded fairly"
    if (index === 1 && credential.includes('heard, valued, and rewarded fairly')) {
      const parts = credential.split(/(heard, valued, and rewarded fairly)/);
      return parts.map((part, i) => {
        if (part === 'heard, valued, and rewarded fairly') {
          return <HighlightedText key={i} delay={0.4}>{part}</HighlightedText>;
        }
        return part;
      });
    }
    // Credential 3: highlight "every voice"
    if (index === 2 && credential.includes('every voice')) {
      const parts = credential.split(/(every voice)/);
      return parts.map((part, i) => {
        if (part === 'every voice') {
          return <HighlightedText key={i} delay={0.6}>{part}</HighlightedText>;
        }
        return part;
      });
    }
    // Credential 4: highlight "HIPAA-compliant" and "only with your consent"
    if (index === 3 && credential.includes('HIPAA-compliant') && credential.includes('only with your consent')) {
      const parts = credential.split(/(HIPAA-compliant|only with your consent)/);
      return parts.map((part, i) => {
        if (part === 'HIPAA-compliant') {
          return <HighlightedText key={i} delay={0.8}>{part}</HighlightedText>;
        } else if (part === 'only with your consent') {
          return <HighlightedText key={i} delay={1.0}>{part}</HighlightedText>;
        }
        return part;
      });
    }
    return credential;
  };

  const CheckIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="11" fill="#EBA09B" fillOpacity="0.2" stroke="#EBA09B" strokeWidth="2"/>
      <path d="M7 12L10 15L17 8" stroke="#272726" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <section
      ref={sectionRef}
      style={{
        backgroundColor: backgroundColor,
        padding: '120px 20px 100px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Wavy top divider */}
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '80px',
          pointerEvents: 'none'
        }}
        viewBox="0 0 1440 80"
        preserveAspectRatio="none"
      >
        <path
          d="M0,80 C200,40 400,70 600,50 C800,30 1000,60 1200,40 C1320,25 1380,45 1440,30 L1440,0 L0,0 Z"
          fill="#f7f5f3"
        />
      </svg>

      {/* Large organic background shape */}
      <div
        style={{
          position: 'absolute',
          top: '10%',
          left: '-15%',
          width: '600px',
          height: '600px',
          borderRadius: '60% 40% 50% 50% / 50% 50% 50% 50%',
          background: 'linear-gradient(135deg, rgba(168, 197, 197, 0.15) 0%, rgba(181, 201, 168, 0.08) 100%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
          transform: 'rotate(-10deg)'
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-10%',
          right: '-10%',
          width: '450px',
          height: '450px',
          borderRadius: '50% 50% 40% 60% / 60% 40% 50% 50%',
          background: 'linear-gradient(225deg, rgba(235, 160, 155, 0.15) 0%, rgba(245, 185, 165, 0.08) 100%)',
          filter: 'blur(50px)',
          pointerEvents: 'none'
        }}
      />

      {/* Wavy bottom */}
      <svg
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: '60px',
          pointerEvents: 'none'
        }}
        viewBox="0 0 1440 60"
        preserveAspectRatio="none"
      >
        <path
          d="M0,20 C240,50 480,10 720,35 C960,60 1200,20 1440,40 L1440,60 L0,60 Z"
          fill="#faf9f9"
        />
      </svg>
      <div
        style={{
          maxWidth: '1100px',
          margin: '0 auto',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '60px',
          position: 'relative',
          zIndex: 1
        }}
      >
        {/* Image Side */}
        <div
          style={{
            flex: '1',
            minWidth: '300px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          {imageUrl ? (
            <>
              <div
                style={{
                  maxWidth: '380px',
                  width: '100%',
                  aspectRatio: '3/4',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                  position: 'relative'
                }}
              >
                <img
                  src={imageUrl}
                  alt={imagePlaceholderText}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center top'
                  }}
                />
                {/* Subtle gradient overlay at bottom */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '30%',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 100%)',
                    pointerEvents: 'none'
                  }}
                />
              </div>
              {imageCaption && (
                <p
                  style={{
                    fontSize: '14px',
                    color: '#666',
                    marginTop: '16px',
                    textAlign: 'center',
                    fontStyle: 'italic'
                  }}
                >
                  {imageCaption}
                </p>
              )}
            </>
          ) : (
            <div
              style={{
                width: '100%',
                maxWidth: '400px',
                aspectRatio: '4/5',
                backgroundColor: '#d0c7c7',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#272726',
                fontSize: '18px',
                fontWeight: '500'
              }}
            >
              {imagePlaceholderText}
            </div>
          )}
        </div>

        {/* Content Side */}
        <div
          style={{
            flex: '1',
            minWidth: '300px',
            textAlign: 'left'
          }}
        >
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(32px, 4vw, 48px)',
              fontWeight: '700',
              color: '#272726',
              marginTop: '0',
              marginBottom: '32px',
              lineHeight: '1.2'
            }}
          >
            {sectionTitle}
          </h2>

          <ul
            style={{
              listStyle: 'none',
              padding: '0',
              margin: '0 0 32px 0'
            }}
          >
            {credentials.map((credential, index) => (
              <li
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  marginBottom: '16px'
                }}
              >
                <div style={{ flexShrink: 0, marginTop: '2px' }}>
                  <CheckIcon />
                </div>
                <span
                  style={{
                    fontSize: '18px',
                    lineHeight: '1.5',
                    color: '#272726'
                  }}
                >
                  {renderCredential(credential, index)}
                </span>
              </li>
            ))}
          </ul>

          <a
            href={ctaLink}
            onClick={(e) => {
              if (onApproachClick) {
                e.preventDefault();
                onApproachClick();
              }
            }}
            style={{
              display: 'inline-block',
              backgroundColor: 'transparent',
              color: '#272726',
              fontSize: '14px',
              fontWeight: '700',
              textTransform: 'uppercase',
              textDecoration: 'none',
              padding: '14px 48px',
              border: '2px solid #272726',
              cursor: 'pointer'
            }}
          >
            {ctaText}
          </a>
        </div>
      </div>
    </section>
  );
};
